import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Clock, Eye, Server, Trash2 } from 'lucide-react';

const Security = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-slate-900 dark:text-white">
            SmartPDF Tools
          </Link>
          <Link 
            to="/" 
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-3">
            Security & Privacy
          </h1>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Your document security is our top priority. Learn how we protect your files and privacy.
          </p>
        </div>

        {/* Main Notice Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-10">
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            All files are processed securely over HTTPS. We never store documents permanently — all uploads and converted files are automatically deleted after a short period. Download links are temporary and protected. Your documents remain private, secure, and inaccessible to anyone but you.
          </p>
        </div>

        {/* Security Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Encrypted Transfer */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Encrypted Transfer</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              All file uploads and downloads are encrypted using TLS/SSL (HTTPS). Your documents are protected during transit and cannot be intercepted.
            </p>
          </div>

          {/* Auto Deletion */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Automatic Deletion</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              All uploaded files and processed results are automatically deleted from our servers within a short period. We never keep your documents.
            </p>
          </div>

          {/* Temporary Links */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Temporary Download Links</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Download links expire automatically. Once your session ends or the link expires, the file becomes inaccessible.
            </p>
          </div>

          {/* No Human Access */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white">No Human Access</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Your documents are processed automatically by our servers. No human ever views, reads, or accesses your files.
            </p>
          </div>

          {/* Secure Processing */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                <Server className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Secure Infrastructure</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Our servers run on secure, isolated infrastructure with strict access controls and regular security audits.
            </p>
          </div>

          {/* Privacy First */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-rose-100 dark:bg-rose-900/30 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Privacy First</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              We collect minimal data necessary to provide our services. We never sell, share, or analyze your document contents.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-5 border border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                How long are my files stored?
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Uploaded files and converted results are automatically deleted within 1 hour. We recommend downloading your files immediately after processing.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg p-5 border border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                Can anyone else access my documents?
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                No. Each file is assigned a unique, random identifier. Without this identifier, files cannot be accessed. Download links are not guessable and expire automatically.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg p-5 border border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                Do you analyze or read my documents?
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                No. Your documents are processed automatically by our conversion algorithms. We never read, analyze, or store the contents of your files.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-lg p-5 border border-slate-200 dark:border-slate-700">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                Is my data shared with third parties?
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                No. We do not sell, share, or provide your files or personal information to any third parties. Your documents remain entirely private.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            <Shield className="w-4 h-4" />
            Start Using SmartPDF Tools
          </Link>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-3">
            Your documents are safe with us.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>© 2025 SmartPDF Tools. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Security;
